import { dayjs, Dayjs } from '@esg/shared';
import { Block, Spacer, TextCommon, useLocalesFormat } from '@esg/ui';
import { FormConnector } from '@esg/ui/components/inputs/FormConnector';
import { DatePicker, Form, DatePickerProps } from 'antd';
import React from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type DatePickerFieldProps = DatePickerProps & {
    readonly name: string;
    readonly label?: string;
    readonly description?: string;
    readonly placeholder?: string;
    readonly rules?: UseControllerProps['rules'];
    readonly formatValue?: 'day' | 'hours' | 'minute' | 'second';
    readonly onHandleChange?: (date?: Dayjs) => void;
};

export const DatePickerField = (props: React.PropsWithChildren<DatePickerFieldProps>) => {
    const { t } = useTranslation();
    const { formatDate } = useLocalesFormat();

    const { name, rules, label, placeholder, formatValue, format, onHandleChange, ...rest } = props;

    const dateFormat = React.useMemo<DatePickerProps['format']>(() => {
        if (format) {
            return format;
        }

        if (rest?.picker === 'week') {
            return (value) => {
                const startOfWeek = value.startOf('week');
                const endOfWeek = value.endOf('week');

                return `${formatDate(startOfWeek)} - ${formatDate(endOfWeek)}`;
            };
        }

        if (rest?.picker === 'date' || !rest?.picker) {
            return (value) => formatDate(value);
        }

        return undefined;
    }, [format, formatDate, rest?.picker]);

    const isRequired = !!rules?.required;

    const onRender = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (renderProps: any) => {
            const { onChange, value } = renderProps.field;
            const { error } = renderProps.fieldState;

            const valueDate = Array.isArray(value) ? value.map(v => dayjs(v)) : dayjs(value);

            return (
                <Form.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message && t(error?.message)}
                >
                    <DatePicker
                        onChange={(value) => {
                            if (Array.isArray(value)) {
                                if (rest?.picker === 'week') {
                                    return onChange(value.map(v => v.startOf('week').startOf('day').toISOString()));
                                }

                                return onChange(value.map(v => v?.toISOString()));
                            }

                            let dateValue = value;

                            if (rest?.picker === 'week') {
                                dateValue = value.startOf('week').startOf('day');
                            }

                            if (formatValue) {
                                return onChange(dateValue?.startOf(formatValue)?.toISOString());
                            }

                            onChange(dateValue?.startOf('minute')?.toISOString());
                            onHandleChange?.(dateValue);
                        }}
                        value={value ? valueDate as Dayjs : value}
                        placeholder={placeholder && t(placeholder)}
                        style={{ width: '100%' }}
                        needConfirm={false}
                        format={dateFormat}
                        {...rest}
                    />
                </Form.Item>
            );
        },
        [formatValue, placeholder, rest, t]
    );

    return (
        <FormConnector>
            {(form) => {
                const { control } = form;
                return (
                    <Block width='100%'>
                        {label && (
                            <Spacer bottom={6}>
                                <TextCommon fontWeight={600} lineHeight='20px' color='#344054'>
                                    {t(label)}
                                </TextCommon>
                                {isRequired && <span style={{ color: 'red' }}> *</span>}
                            </Spacer>
                        )}
                        {props.description && (<Spacer bottom={4}><TextCommon>{t(props.description)}</TextCommon></Spacer>)}
                        <Controller name={name} control={control} render={onRender} rules={rules} />
                    </Block>
                );
            }}
        </FormConnector>
    );
};
